import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/shared/i18n';

interface DeletePackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  packName?: string;
  isLoading?: boolean;
}

export function DeletePackModal({
  isOpen,
  onClose,
  onConfirm,
  packName,
  isLoading = false,
}: DeletePackModalProps) {
  const { t } = useTranslation('packs');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-[#1a1a1a] border-[#333333]">
        <DialogHeader>
          <div className="flex gap-3 items-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <DialogTitle className="text-white text-lg font-semibold">
                {t('deletePack')}
              </DialogTitle>
              <DialogDescription className="text-white/60 text-sm">
                {t('deletePackWarning')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="py-4">
          <p className="text-white/80 text-sm leading-relaxed">
            {t('deletePackMessage', {
              packName: packName || t('deletePackThisPack'),
            })}
          </p>
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
            <p className="text-red-400 text-xs font-medium">
              {t('deletePackIrreversible')}
            </p>
          </div>
        </div>
        <DialogFooter className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60"
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 text-white border-0"
          >
            {isLoading ? t('deletePackDeleting') : t('deletePackConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
